/**
 * AddBusinessPage
 * ───────────────
 * Public "Add Your Business" / "Claim This Listing" onboarding page.
 * Renders the unified manifest-driven <BusinessSignup> component
 * (manifest_id="plugin_au") inside the existing hero shell.
 *
 * iter313c: Fires a `/api/directory/claim-analytics` beacon on open
 * (stage="open") and on success so ops can chart conversion by
 * source venue + suburb.
 */
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import BusinessSignup from "@/components/BusinessSignup";

const API = process.env.REACT_APP_BACKEND_URL ||
            (typeof window !== "undefined" ? window.location.origin : "");

export default function AddBusinessPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isClaim = location.pathname.startsWith("/claim-business");
  const slug = searchParams.get("slug");
  const prefill = slug ? { directory_id: slug, country: "au" } : null;

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      fetch(`${API}/api/directory/claim-analytics`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug,
          referrer: document.referrer || null,
          source_url: window.location.href,
          stage: "open",
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* ignore */ }
  }, [slug, isClaim]);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="add-business-page">
      <header className="bg-gradient-to-r from-amber-600 to-stone-900 text-white">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <Link to="/directory" className="inline-flex items-center gap-1 text-[12px] text-stone-300 hover:text-white">
            <ArrowLeft className="w-3.5 h-3.5" /> Directory
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3">
            {isClaim ? "Claim Your Listing" : "Add Your Business"}
          </h1>
          <p className="text-stone-300 mt-2 max-w-xl text-[14px]">
            {isClaim
              ? "Take ownership of your directory listing — we verify by ABN against the live Australian Business Register, then link you across every HustlePlaza module."
              : "Get your business onto HustlePlaza. ABN-verified, instantly linked across Directory, Plate, Plugin, and beyond."}
          </p>
          <p className="mt-3 text-[11px] uppercase tracking-[0.18em] font-bold text-amber-300">
            HustleConnect · Australia
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <BusinessSignup
          manifestId="plugin_au"
          prefill={prefill}
          onSubmit={(result) => {
            if (result?.directory_id && typeof window !== "undefined") {
              try {
                window.sessionStorage.setItem("hp_last_signup_directory_id", result.directory_id);
              } catch (_) { /* ignore */ }
            }
            try {
              fetch(`${API}/api/directory/claim-analytics`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  slug: result?.directory_id || slug,
                  stage: "success",
                }),
                keepalive: true,
              }).catch(() => {});
            } catch (_) { /* ignore */ }
          }}
        />

        <div className="mt-8 rounded-xl border border-stone-200 bg-white p-4 text-[12px] text-stone-600">
          <p className="font-bold text-stone-800 mb-1">What happens next</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Your business is upserted into the Australian directory.</li>
            <li>If your ABN matches an existing entity, we link you across all HustlePlaza modules.</li>
            <li>For WordPress plugin users — we&apos;ll guide you through SMS + email verification in the plugin admin.</li>
          </ol>
        </div>
      </main>
    </div>
  );
}
